import type {
  AudienceChannel,
  CampaignFilterInput,
  CampaignFilterRule,
} from './contracts';

export interface CampaignAudienceSuppression {
  reason: string;
  count: number;
}

export interface CampaignAudienceChannelPreview {
  channel: AudienceChannel;
  eligible: number;
  suppressed: CampaignAudienceSuppression[];
  notes: string[];
}

export interface CampaignAudienceFreshness {
  generatedAt: string;
  cutoffAt: string;
  latestRawIngestedAt: string | null;
  latestActivityAt: string | null;
  sourceLagHours: number | null;
  edrHistoryStartedAt: string | null;
}

export interface CampaignAudienceCoverage {
  rawEventCount: number | null;
  deduplicatedEventCount: number | null;
  classifiedEventCount: number | null;
  unmatchedEventCount: number | null;
  accountCount: number | null;
  appUserCount: number | null;
  dataIdentifierCoverage: number | null;
  voiceIdentifierCoverage: number | null;
  smsIdentifierCoverage: number | null;
  otherChargeCoverage: number | null;
}

export interface CampaignAudienceQuality {
  warningCount: number;
  qualityWarnings: string[];
  failureCode: string | null;
  accountMatchRate: number | null;
  dailyEventMismatchCount: number | null;
  missingNetworkSource: string | null;
}

export interface CampaignAudienceBrandPreview {
  mvnoId: string;
  brandLabel: string;
  snapshotId?: string;
  cutoffAt?: string;
  generatedAt?: string;
  latestEdrIngestedAt?: string | null;
  activityDataStatus?: 'AVAILABLE' | 'UNAVAILABLE';
  freshness?: CampaignAudienceFreshness;
  coverage?: CampaignAudienceCoverage;
  quality?: CampaignAudienceQuality;
  channels: CampaignAudienceChannelPreview[];
  code?: string;
  errorCode?: string;
  error?: string;
}

export interface CampaignPreviewErrorEvidence {
  code?: unknown;
  errorCode?: unknown;
  error?: unknown;
}

export type CampaignAudienceMode = 'snapshot' | 'legacy-live';

export interface CampaignAudiencePreviewResponse {
  audienceMode?: CampaignAudienceMode;
  previewId?: string;
  filterHash?: string;
  filter?: CampaignFilterInput;
  expiresAt?: string;
  brands: CampaignAudienceBrandPreview[];
}

type BoundCampaignAudiencePreviewResponse = Omit<
  CampaignAudiencePreviewResponse,
  'audienceMode'
> & { audienceMode: CampaignAudienceMode };

export interface CampaignTargetingSelection {
  audienceMode: CampaignAudienceMode;
  mvnoIds: readonly string[];
  channels: readonly AudienceChannel[];
  filters: CampaignFilterInput;
  activeWithinDays?: number | null;
}

interface BoundCampaignAudiencePreviewBase {
  audienceMode: CampaignAudienceMode;
  selectionKey: string;
  response: BoundCampaignAudiencePreviewResponse;
}

export interface BoundSnapshotCampaignAudiencePreview extends BoundCampaignAudiencePreviewBase {
  audienceMode: 'snapshot';
  previewId: string;
  filterHash: string;
  expiresAt: string;
}

export interface BoundLegacyCampaignAudiencePreview extends BoundCampaignAudiencePreviewBase {
  audienceMode: 'legacy-live';
}

export type BoundCampaignAudiencePreview =
  | BoundSnapshotCampaignAudiencePreview
  | BoundLegacyCampaignAudiencePreview;

export type CampaignSendMode = 'now' | 'schedule';

const SHA_256_HEX = /^[a-f0-9]{64}$/;

const stalePreviewCodes = new Set([
  'EXPIRED_PREVIEW',
  'PREVIEW_EXPIRED',
  'SNAPSHOT_EXPIRED',
  'SNAPSHOT_STALE',
  'STALE_SNAPSHOT',
]);
const configurationPreviewCodes = new Set([
  'CONFIGURATION_ERROR',
  'MISSING_CONSENT_COLUMN',
  'SCHEMA_MISMATCH',
]);
const unavailablePreviewCodes = new Set([
  'ACTIVITY_SOURCE_UNAVAILABLE',
  'PERMISSION_DENIED',
  'SERVICE_UNAVAILABLE',
  'SOURCE_UNAVAILABLE',
]);

/** Closed public disclosure boundary for backend audience-preview failures. */
export function publicPreviewErrorMessage(
  evidence: CampaignPreviewErrorEvidence,
): string {
  const rawCode = evidence.errorCode ?? evidence.code;
  const code = typeof rawCode === 'string' ? rawCode.trim().toUpperCase() : '';
  if (stalePreviewCodes.has(code))
    return 'Audience snapshot is stale. Refresh the preview.';
  if (configurationPreviewCodes.has(code))
    return 'Audience source configuration is incomplete for this brand. Contact support.';
  if (unavailablePreviewCodes.has(code))
    return 'Audience source is unavailable for this brand. Try again later.';

  const error =
    typeof evidence.error === 'string' ? evidence.error.toLowerCase() : '';
  if (/\b(stale|expired)\b/.test(error))
    return 'Audience snapshot is stale. Refresh the preview.';
  if (
    /\b(consent|column|schema)\b|unrecognized name|no such field|missing\s+(?:column|field)|(?:table|dataset)\s+not found/.test(
      error,
    )
  )
    return 'Audience source configuration is incomplete for this brand. Contact support.';
  if (
    /permission|access denied|forbidden|unavailable|timed?\s*out|\bprobe\b|\bbigquery\b/.test(
      error,
    )
  )
    return 'Audience source is unavailable for this brand. Try again later.';
  return 'Audience preview is unavailable for this brand. Contact support if this persists.';
}

export function bindPreview(
  response: CampaignAudiencePreviewResponse,
  selection: CampaignTargetingSelection,
): BoundCampaignAudiencePreview | null {
  const responseAudienceMode = response.audienceMode ?? 'legacy-live';
  const normalizedResponse: BoundCampaignAudiencePreviewResponse = {
    ...response,
    audienceMode: responseAudienceMode,
  };
  const previewId = response.previewId?.trim();
  const filterHash = response.filterHash?.trim();
  const expiresAt = response.expiresAt?.trim();
  const selectedMvnoIds = normalizedStrings(selection.mvnoIds);
  const selectedChannels = normalizedStrings(selection.channels);
  const responseMvnoIds = normalizedStrings(
    response.brands.map((brand) => brand.mvnoId),
  );
  const completeChannelEvidence = response.brands.every(
    (brand) =>
      !brand.error &&
      equalStrings(
        normalizedStrings(brand.channels.map((entry) => entry.channel)),
        selectedChannels,
      ),
  );
  if (
    responseAudienceMode !== selection.audienceMode ||
    !completeChannelEvidence ||
    !equalStrings(responseMvnoIds, selectedMvnoIds)
  ) {
    return null;
  }
  if (responseAudienceMode === 'legacy-live') {
    return {
      audienceMode: 'legacy-live',
      selectionKey: targetingSelectionKey(selection),
      response: normalizedResponse,
    };
  }
  const completeSnapshotEvidence = response.brands.every((brand) =>
    Boolean(brand.snapshotId?.trim()),
  );
  if (
    !previewId ||
    !filterHash ||
    !SHA_256_HEX.test(filterHash) ||
    !expiresAt ||
    !Number.isFinite(Date.parse(expiresAt)) ||
    !response.filter ||
    !completeSnapshotEvidence ||
    canonicalFilterKey(response.filter) !==
      canonicalFilterKey(selection.filters)
  ) {
    return null;
  }
  return {
    audienceMode: 'snapshot',
    previewId,
    filterHash,
    expiresAt,
    selectionKey: targetingSelectionKey(selection),
    response: normalizedResponse,
  };
}

export function isPreviewCurrent(
  preview: BoundCampaignAudiencePreview | null,
  selection: CampaignTargetingSelection,
  nowMs = Date.now(),
): preview is BoundCampaignAudiencePreview {
  return Boolean(
    preview &&
    preview.selectionKey === targetingSelectionKey(selection) &&
    (preview.audienceMode === 'legacy-live' ||
      (SHA_256_HEX.test(preview.filterHash) &&
        Date.parse(preview.expiresAt) > nowMs)),
  );
}

export function sendPayload(
  preview: BoundCampaignAudiencePreview | null,
  selection: CampaignTargetingSelection,
  mode: CampaignSendMode,
  nowMs = Date.now(),
):
  | { activeWithinDays: number | null }
  | { filters: CampaignFilterInput; previewId?: string } {
  const targeting =
    selection.audienceMode === 'legacy-live'
      ? { activeWithinDays: selection.activeWithinDays ?? null }
      : { filters: selection.filters };
  if (mode === 'schedule') return targeting;
  if (!isPreviewCurrent(preview, selection, nowMs)) {
    throw new Error('Preview this exact audience before sending.');
  }
  return preview.audienceMode === 'snapshot'
    ? { previewId: preview.previewId, filters: selection.filters }
    : targeting;
}

export function previewCountLabel(
  count: number,
  mode: CampaignSendMode,
): string {
  const eligible = `${count.toLocaleString('en-US')} eligible`;
  return mode === 'schedule'
    ? `Estimate — recalculated at release: ${eligible}`
    : eligible;
}

function targetingSelectionKey(selection: CampaignTargetingSelection): string {
  return JSON.stringify({
    audienceMode: selection.audienceMode,
    mvnoIds: normalizedStrings(selection.mvnoIds),
    channels: normalizedStrings(selection.channels),
    ...(selection.audienceMode === 'snapshot'
      ? { filter: canonicalFilter(selection.filters) }
      : { activeWithinDays: selection.activeWithinDays ?? null }),
  });
}

function canonicalFilterKey(filter: CampaignFilterInput): string {
  return JSON.stringify(canonicalFilter(filter));
}

function canonicalFilter(filter: CampaignFilterInput): CampaignFilterInput {
  const rules = filter.rules
    .map(canonicalRule)
    .sort((left, right) =>
      JSON.stringify(left).localeCompare(JSON.stringify(right)),
    );
  return { version: 1, logic: 'AND', rules };
}

function canonicalRule(rule: CampaignFilterRule): CampaignFilterRule {
  return {
    field: rule.field,
    operator: rule.operator,
    ...(rule.service === undefined ? {} : { service: rule.service }),
    ...(rule.value === undefined ? {} : { value: rule.value }),
    ...(rule.values === undefined
      ? {}
      : { values: normalizedStrings(rule.values) }),
  };
}

function normalizedStrings(values: readonly string[]): string[] {
  return [
    ...new Set(values.map((value) => value.trim()).filter(Boolean)),
  ].sort();
}

function equalStrings(
  left: readonly string[],
  right: readonly string[],
): boolean {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
}
